import React, { useState } from 'react'
import ReactModal from 'react-modal'
import { displayToast } from '@Utils/toast'
import { Tooltip } from 'react-tooltip'
import { useUpgradeContext } from '@Utils/common'
import { RootState, useAppSelector } from '@Store/store'

const AddResponse = ({ add_component, response_data }: { add_component: (component: any) => void, response_data: any }) => {

  const { setUpgradeToPro } = useUpgradeContext();
  const { user } = useAppSelector((state: RootState) => state.profile);

  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleOpenModal = () => {
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const handleOptionClick = (option: any) => {
    if (option.is_pro && !user?.is_pro) {
      // displayToast('warning',  "Upgrade Now! To keep using this PRO feature afte 15th Aug")
      setUpgradeToPro(true);
      handleCloseModal()
      return
    };

    if (option.id == "ask_follow" || option.id == "user_input") {
      for (let item of (response_data?.data || [])) {
        if (item?.type === option.component.type) {
          displayToast("error", `${option.title} is already added in the response`)
          setIsModalOpen(false)
          return
        }
      }
    }
    console.log(`Selected option: ${option.component}`)
    // Handle the selected option here
    add_component(option.component)
    setIsModalOpen(false)
  }

  const openingEnabled = response_data?.opening_message?.enabled

  const hasAskFollow = (response_data?.data || []).some((i: any) => i?.type === 'ask_follow')
  const hasUserInput = (response_data?.data || []).some((i: any) => i?.type === 'user_input')
  const hasTextMessage = (response_data?.data || []).some((i: any) => i?.type === 'text_message')
  const hasCardMessage = (response_data?.data || []).some((i: any) => i?.type === 'card_message')

  const responseOptions = [
    {
      id: 'ask_follow',
      title: 'Ask For Follow',
      subtitle: 'Request users to follow your account',
      component: {
        type: 'ask_follow',
      },
      is_pro: true,
    },
    {
      id: 'card_message',
      title: 'Card Message',
      subtitle: 'Send a rich card with Image, Texts and Button',
      component: {
        type: 'card_message',
        data: {
          cards: [{
            title: '',
            subtitle: '',
            image_url: '',
            buttons: []
          }]
        }
      }
    },
    {
      id: 'text_message',
      title: 'Text Message',
      subtitle: 'Send a simple Text or Button Response',
      component: {
        type: 'text_message'
      }
    },
    {
      id: 'user_input',
      title: 'Lead Forms',
      subtitle: 'Request users to input text',
      component: {
        type: 'user_input',
        enabled: false,
      },
      is_pro: true,
    }
  ]

  if (user.user_id == 11764) {
    responseOptions.push(
      {
        id: 'send_to_meragi_api',
        title: 'Send to Meragi API',
        subtitle: 'Send user data to Meragi API endpoint',
        component: {
          type: 'send_to_meragi_api',
        },
        is_pro: false,
      })
  }

  return (
    <div className="w-full mt-2">
      {/* Full-width button */}
      <button
        onClick={handleOpenModal}
        className="w-full py-4 px-6 mt-4 bg-[#3535d5] hover:bg-[#7373ff] text-white font-medium rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
      >
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4v16m8-8H4"
          />
        </svg>
        Add Response
      </button>

      {/* ReactModal */}
      <ReactModal
        isOpen={isModalOpen}
        onRequestClose={handleCloseModal}
        className="m-2 relative max-w-md w-full p-6 rounded-xl border-none bg-white"
        overlayClassName="z-50 fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
        ariaHideApp={false}
      >
        <div className="space-y-4">
          {/* Modal Header */}
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Add Response</h2>
            <button
              onClick={handleCloseModal}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg
                className="w-5 h-5 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Contextual helper */}
          {!openingEnabled && (
            <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm">
              Opening message is turned off, only one of Text or Card is allowed
            </div>
          )}

          {/* Modal Content */}
          <div className="space-y-3">
            {responseOptions.map((option) => {
              const isSpecial = option.id === 'ask_follow' || option.id === 'user_input'
              // When opening is disabled, only one of text/card may exist total
              const restrictToSingleBasic = !openingEnabled && (hasTextMessage || hasCardMessage)
              const isText = option.id === 'text_message'
              const isCard = option.id === 'card_message'
              const disabled =
                (!openingEnabled && isSpecial) ||
                (option.id === 'ask_follow' && hasAskFollow) ||
                (option.id === 'user_input' && hasUserInput) ||
                (restrictToSingleBasic && (isText || isCard))

              let disabledReason: string | null = null
              if (!openingEnabled && isSpecial) {
                disabledReason = 'Requires Opening Message'
              } else if (option.id === 'ask_follow' && hasAskFollow) {
                disabledReason = 'Already added'
              } else if (option.id === 'user_input' && hasUserInput) {
                disabledReason = 'Already added'
              } else if (restrictToSingleBasic && (isText || isCard)) {
                disabledReason = 'Opening Message is off, only one of Text/Card allowed'
              }
              return (
                <button
                  key={option.id}
                  onClick={() => !disabled && handleOptionClick(option)}
                  disabled={disabled}
                  className={`w-full text-left p-4 rounded-lg border transition-all duration-200 group ${disabled ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className={`font-medium ${disabled ? 'text-gray-400' : 'text-gray-900 group-hover:text-blue-700'}`}>
                          {option.title}
                        </h3>
                        {option.is_pro && <div>
                          <Tooltip anchorSelect=".pro-feature" style={{ backgroundColor: '#ffc125', textAlign: 'center', color: 'black' }} place="top">
                            <b>PRO Feature!</b>
                          </Tooltip>
                          <button className="pro-feature mr-3 bg-[linear-gradient(265.93deg,#ffc125_-27.12%,#ffd86e,#ffbe18_125.94%)] flex-shrink-0 px-4 py-1 rounded-xl">
                            <img alt="" className="h-4 w-4" src="https://in.imitari.app/zorcha/premium-icon-crown.svg?tr=f-webp,w-600" />
                          </button>
                        </div>}
                      </div>
                      {/* {isSpecial && !openingEnabled && (
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${disabled ? 'border-gray-300 text-gray-400' : 'border-blue-300 text-blue-600'}`}>
                          Needs Opening Message
                        </span>
                      )} */}
                      <p className={`text-sm mt-1 ${disabled ? 'text-gray-400' : 'text-gray-500'}`}>
                        {option.subtitle}
                      </p>
                      {disabled && disabledReason && (
                        <div className="mt-2 text-xs text-red-500">
                          {disabledReason}
                        </div>
                      )}
                    </div>
                    <svg
                      className={`w-5 h-5 mt-0.5 ${disabled ? 'text-gray-300' : 'text-gray-400 group-hover:text-blue-500'}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>

                </button>
              )
            })}
          </div>
        </div>
      </ReactModal>
    </div>
  )
}

export default AddResponse